import { React, PureComponent, PropTypes, reduxConnect, ThemeProvider } from '../lib/imports';
import { throttle } from 'lodash';

import { BaseStyles } from '../components';
import { ConfigProvider } from '../app/context/config';
import { setCurrentRoute } from '../routing/actions';
import { pathMap } from '../routing/routeManager';
import { setLanguage } from '../i18n/actions';

import {
  setIsFetching,
  setIsNotFetching,
  setElementFocused,
  browserIsActive,
  hasFetchError,
  incrementFetchErrorCount,
} from '../ui/actions';
import { fetchedConfig, sessionIsCurrent } from './configure/actions';
import { fetchQuizData, fetchUserData, fetchNotificationStatus } from '../onboarding/actions';
import { fetchHistoryData, fetchUserStats } from '../heatStreak/actions';
import { fetchPlayerData } from '../players/actions';
import {
  fetchLeaderboardData,
  fetchLocalLeaderboardData,
  fetchIsCurrentlySeason,
  fetchIsCurrentlyPlayoffs,
} from '../leaderboard/actions';
import { fireMessageEvent } from '../messenger/utils';
import { DFPSlotsProvider } from 'react-dfp';
import { batch } from 'react-redux';

import Layout from '../app/Layout';
import Loader from '../ui/Loader';
import GetNewApp from '../pages/GetNewApp';

import { isDateAfter } from '../lib/date';
import { SEASON_CODE, SEASON_END_DATE } from '../lib/constants';

class App extends PureComponent {
  scrollTimeout = 100;

  componentDidMount = () => {
    const { config, handleSetCurrentLanguage, hasFetchError, handleBrowserIsActive } = this.props;

    this.scrollContainer = document.getElementById('thLayout');
    if (this.scrollContainer) {
      this.scrollContainer.addEventListener('scroll', throttle(this.handlePageScroll, this.scrollTimeout));
    }

    // testing the Page visibility api
    document.addEventListener('visibilitychange', handleVisibilityChange, false);

    // setup message event from parent
    window.addEventListener('message', this.handleMessage);

    function handleVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        handleBrowserIsActive(false);
      } else {
        handleBrowserIsActive(true);
      }
    }

    if (hasFetchError) {
      return null;
    }

    handleSetCurrentLanguage(config.language);

    if (!config.currentSession) {
      fireMessageEvent({
        type: 'AUTH_FAILURE',
        platform: this.props.platform,
        env: this.props.env,
        title: 'fireMessageEvent',
        data: {},
      });
    }
  };

  componentWillUnmount = () => {
    if (this.scrollContainer) {
      this.scrollContainer.removeEventListener('scroll', this.handlePageScroll);
    }
    window.removeEventListener('message', this.handleMessage);
  };

  handleMessage = (evt) => {
    const { data } = evt;
    let dObj;

    try {
      if (data && typeof data === 'string') {
        dObj = JSON.parse(data);
      }

      switch (dObj.type) {
        case 'AUTH_REFRESH':
          if (this.props.fetchErrorCount < 3) {
            batch(() => {
              this.props.handleIncrementFetchErrorCount(this.props.fetchErrorCount + 1);
              this.props.handleSessionIsCurrent(dObj.message);
              this.props.handleHasFetchError();
            });
          }
          break;
        default:
          break;
      }
    } catch (err) {
      // TODO handle any errors not covered in actions
    }
  };

  handlePageScroll = () => {
    const { isFocused, scrollPositionForPicks, handleSetElementFocused } = this.props;

    const scrollTop = this.scrollContainer ? this.scrollContainer.scrollTop : 0;
    const scrollDelta = Math.abs(Math.floor(scrollTop) - scrollPositionForPicks);

    const scrollAtPicks = Math.floor(scrollTop) === scrollPositionForPicks || (scrollDelta >= 0 && scrollDelta <= 5);

    if (scrollAtPicks) {
      handleSetElementFocused({
        picks: true,
      });
    } else if (isFocused.picks) {
      handleSetElementFocused({
        picks: false,
      });
    }
  };

  componentDidUpdate = async (prevProps) => {
    const {
      config,
      handleFetchUserData,
      handlefetchedConfig,
      handleFetchQuizData,
      handleFetchNotificationStatus,
      handleSetIsFetching,
      handleSetIsNotFetching,
      handleFetchHistoryData,
      handleFetchUserStats,
      handleFetchPlayerData,
      handleFetchLeaderboardData,
      handleFetchIsCurrentlyPlayoffs,
      handleFetchIsCurrentlySeason,
      handlefetchLocalLeaderboardData,
      handleSetCurrentRoute,
      isOnboardingActive,
      hasBadPlayerData,
      browserIsActive,
      menuLinkClicked,
      hasFetchError,
      hasFetchedConfig,
      selectedBoard,
      selectedChallenge,
      fetchErrorCount,
      heatStreakPanelOpen,
    } = this.props;

    if (hasFetchedConfig && hasFetchError && fetchErrorCount < 3) {
      fireMessageEvent({ type: 'AUTH_FAILURE', title: 'fireMessageEvent', data: {} });
    }

    if (config.currentSession) {
      if (config.userId && config.currentSession && !hasFetchedConfig) {
        handlefetchedConfig();
        await Promise.all([handleFetchQuizData(), handleFetchUserData(), handleFetchNotificationStatus()]);
        handleSetIsNotFetching();
      }

      if (isOnboardingActive && prevProps.currentRoute === 'onboarding') {
        return null;
      }

      if (prevProps.menuLinkClicked !== menuLinkClicked) {
        document.getElementById('thLayout').scrollTo(0, 0);
      }

      const seasonEnded = isDateAfter(SEASON_END_DATE);
      const season = selectedBoard === 'monthly' && seasonEnded ? SEASON_CODE : null;

      if (!isOnboardingActive && browserIsActive && browserIsActive !== prevProps.browserIsActive) {
        handleSetCurrentRoute(pathMap(config.routeBase)[window.location.pathname.replace(/\/$/, '')]);
        const challenge = selectedBoard === 'monthly' ? selectedChallenge : null;
        await Promise.all([
          handleFetchHistoryData(),
          handleFetchUserStats(),
          handleFetchPlayerData(),
          handleFetchLeaderboardData(selectedBoard, challenge, season),
          handlefetchLocalLeaderboardData(selectedBoard, true, challenge, season),
        ]);
      }

      if (prevProps.menuLinkClicked !== menuLinkClicked) {
        handleSetCurrentRoute(pathMap(config.routeBase)[window.location.pathname.replace(/\/$/, '')]);
        await Promise.all([handleFetchHistoryData(), handleFetchUserStats(), handleFetchPlayerData()]);
      }

      if (
        prevProps.hasBadPlayerData !== hasBadPlayerData ||
        (prevProps.isOnboardingActive && !isOnboardingActive) ||
        (prevProps.hasFetchError !== hasFetchError && !hasFetchError && !heatStreakPanelOpen)
      ) {
        handleSetCurrentRoute(pathMap(config.routeBase)[window.location.pathname.replace(/\/$/, '')]);

        handleSetIsFetching();

        await Promise.all([
          handleFetchHistoryData(),
          handleFetchUserStats(),
          handleFetchPlayerData(),
          handleFetchLeaderboardData(selectedBoard, selectedChallenge, season),
          handlefetchLocalLeaderboardData(selectedBoard, true, selectedChallenge, season),
          handleFetchIsCurrentlySeason(),
          handleFetchIsCurrentlyPlayoffs(),
        ]);

        handleSetIsNotFetching();
      }
    }
  };

  render = () => {
    const { theme, config, isFetching, dfpNetworkId } = this.props;
    const { platform, env } = config;

    return (
      <ThemeProvider theme={theme}>
        <DFPSlotsProvider
          data-testid='google-ad-manager-provider'
          dfpNetworkId={dfpNetworkId || ''}
          lazyLoad
          personalizedAds={false}
          collapseEmptyDivs
        >
          <ConfigProvider config={config}>
            <BaseStyles />
            {isFetching ? <Loader isFixed /> : platform === 'web' && env !== 'dev' ? <GetNewApp /> : <Layout />}
          </ConfigProvider>
        </DFPSlotsProvider>
      </ThemeProvider>
    );
  };
}

App.propTypes = {
  isFetching: PropTypes.number,
  theme: PropTypes.object.isRequired,
  config: PropTypes.object.isRequired,
  isFocused: PropTypes.object,
  scrollPositionForPicks: PropTypes.number,
  hasFetchError: PropTypes.bool,
  hasBadPlayerData: PropTypes.bool,
  handleSetCurrentLanguage: PropTypes.func.isRequired,
  handleFetchUserData: PropTypes.func.isRequired,
  handleFetchQuizData: PropTypes.func.isRequired,
  handleSetIsFetching: PropTypes.func,
  handleSetIsNotFetching: PropTypes.func,
  handleFetchPlayerData: PropTypes.func,
  handleFetchHistoryData: PropTypes.func,
  handleFetchUserStats: PropTypes.func,
  handleFetchLeaderboardData: PropTypes.func,
  handleFetchIsCurrentlySeason: PropTypes.func,
  handleFetchIsCurrentlyPlayoffs: PropTypes.func,
  handlefetchLocalLeaderboardData: PropTypes.func,
  handleSetCurrentRoute: PropTypes.func,
  handleSetElementFocused: PropTypes.func,
  handleFetchNotificationStatus: PropTypes.func,
  currentRoute: PropTypes.string,
  token: PropTypes.string,
  handleCurrentRoute: PropTypes.string,
  isOnboardingActive: PropTypes.bool,
  dfpNetworkId: PropTypes.string,
  handleSessionIsCurrent: PropTypes.func,
  heatStreakPanelOpen: PropTypes.bool,
};

const mapStateToProps = (state) => {
  const { config, ui, routing, onboarding, picks, leaderboard } = state;
  const { initialStep, onboardingStep } = onboarding;
  const { menuLinkClicked } = routing;
  const { fetchError } = picks;
  const {
    isFetching,
    hasFetchError,
    isScrolling,
    notificationBarOpen,
    componentSize,
    isFocused,
    browserIsActive,
    fetchErrorCount,
    heatStreakPanelOpen,
  } = ui;
  const { platform, hasFetchedConfig, env } = config;
  const { picksScrollPosition } = componentSize;
  const { withNotification, withoutNotification } = picksScrollPosition;
  const { selectedChallenge, selectedBoard, error } = leaderboard;

  const scrollPositionForPicks = notificationBarOpen ? withNotification : withoutNotification;

  return {
    isFetching,
    hasFetchError,
    isScrolling,
    config,
    menuLinkClicked,
    isOnboardingActive: !(hasFetchError || initialStep >= 3 || onboardingStep >= 4),
    hasBadPlayerData: !!fetchError || error,
    scrollPositionForPicks,
    isFocused,
    browserIsActive,
    selectedChallenge,
    platform,
    env,
    hasFetchedConfig,
    selectedBoard,
    fetchErrorCount,
    heatStreakPanelOpen,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    handleFetchUserData: () => dispatch(fetchUserData()),
    handleFetchQuizData: () => dispatch(fetchQuizData()),
    handlefetchedConfig: () => dispatch(fetchedConfig()),
    handleSetIsFetching: () => dispatch(setIsFetching()),
    handleSetIsNotFetching: () => dispatch(setIsNotFetching()),
    handleSetCurrentLanguage: (language) => dispatch(setLanguage(language)),
    handleSetCurrentRoute: (id) => dispatch(setCurrentRoute(id)),
    handleFetchHistoryData: () => dispatch(fetchHistoryData()),
    handleFetchUserStats: () => dispatch(fetchUserStats()),
    handleFetchPlayerData: () => dispatch(fetchPlayerData()),
    handleFetchLeaderboardData: (board, challengeNumber, season) =>
      dispatch(fetchLeaderboardData(board, challengeNumber, season)),
    handleFetchIsCurrentlySeason: () => dispatch(fetchIsCurrentlySeason()),
    handleFetchIsCurrentlyPlayoffs: () => dispatch(fetchIsCurrentlyPlayoffs()),
    handlefetchLocalLeaderboardData: (board, isLocal, challengeNumber) =>
      dispatch(fetchLocalLeaderboardData(board, isLocal, challengeNumber)),
    handleSetElementFocused: (element) => dispatch(setElementFocused(element)),
    handleFetchNotificationStatus: () => dispatch(fetchNotificationStatus()),
    handleBrowserIsActive: (isActive) => dispatch(browserIsActive(isActive)),
    handleSessionIsCurrent: (jwt) => dispatch(sessionIsCurrent(jwt)),
    handleHasFetchError: () => dispatch(hasFetchError()),
    handleIncrementFetchErrorCount: (value) => dispatch(incrementFetchErrorCount(value)),
  };
};

export default reduxConnect(mapStateToProps, mapDispatchToProps)(App);
